import React from 'react';
import { motion } from 'framer-motion';

const LEAKS = [
  {
    num: '01',
    title: 'What moved this week — and why',
    desc: 'Deterministic revenue, margin and channel bridges — with an AI-written briefing written over them. Every rupee in the narrative literally exists in the numbers; nothing is invented. Written to be forwarded without a single edit.',
  },
  {
    num: '02',
    title: 'Settlement Variances',
    desc: 'Aggregator payouts (Swiggy, Zomato) never match your POS sales — and most of that gap is expected, buried in commission, GST, and platform fees most owners never calculate. A real slice of it isn\'t structural — it\'s wrong commission tiers, auto-applied discounts, or cancelled orders billed as delivered. Catching the difference requires reconciling settlement reports before the weekly dispute window closes.',
  },
  {
    num: '03',
    title: 'Supplier Overcharges',
    desc: 'Vendors raise rates between invoices, swap SKUs, or invoice quantities not delivered. Every line is benchmarked against your contracted rate and yesterday\'s market price — overcharges come with a ready-to-send negotiation brief.',
  },
  {
    num: '04',
    title: 'Reconciliation Gaps',
    desc: 'When your sales records, accounting books and bank deposits do not agree, money slips through the gaps. Three-way reconciliation matches every transaction across all sources so nothing is missed.',
  },
  {
    num: '05',
    title: 'Bank Deposit Shortfalls',
    desc: 'Settlement fees, MDR, GST, and TDS are individually documented — but never linked back to the specific order or bill they came from. A single bank credit can quietly net out hundreds of transactions, so the only way to know if the deposit is correct (not just lower than expected) is to reconstruct expected vs. actual, order by order.',
  },
  {
    num: '06',
    title: 'Card vs Bill Mismatches',
    desc: 'Voided bills, missed tenders and cash-drawer shortfalls compound silently at the shift level. Even a few thousand a day represents lakhs a year per outlet.',
  },
  {
    num: '07',
    title: 'Menu Mis-pricing',
    desc: 'Margin is often left on the table when highest-margin items are discounted on instinct and lowest-margin items are promoted on popularity alone. Menu economics calls for evidence, not intuition.',
  },
  {
    num: '08',
    title: 'Unanalysed Customer Feedback',
    desc: 'Repeated complaints about specific dishes, pricing or service patterns get lost in review noise — until they show up as next quarter\'s revenue drop.',
  },
  {
    num: '09',
    title: 'Reconciled sources, or clearly Unverified',
    desc: 'Where a source isn\'t on file, we say so — not fabricate a number to fill the gap. UTRs without a bank statement to check against are marked Unverified, not Missing. The number you defend to your investor is the number we\'ll defend to a court.',
  },
  {
    num: '10',
    title: 'A weekly + monthly rhythm',
    desc: 'Weekly Brief every Monday 08:00. Monthly Detailed on the 7th. Two artefacts, one voice — designed to be forwarded to investors, auditors, or your own team without a single edit.',
  },
  {
    num: '11',
    title: 'Ambiguous P&L categories',
    desc: 'Every rupee categorised — no black-box "Other" absorbing what nobody wanted to sort. Cost outliers surface against a rolling baseline, and every line ties back to the receipt it came from.',
  },
];

const HEADLINE_WORDS = [
  { text: 'Eleven', italic: false },
  { text: 'things', italic: false },
  { text: 'a', italic: false },
  { text: 'real weekly read', italic: true, color: '#7A5F2E' },
  { text: 'surfaces', italic: false },
  { text: '—', italic: false },
  { text: 'before', italic: false },
  { text: 'month-end.', italic: false },
];

const wordFadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

const cardFadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.05 + i * 0.06, duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const LeaksGrid = () => (
  <section
    id="problem"
    data-testid="landing-leaks"
    className="relative py-28 md:py-40 overflow-hidden"
    style={{ background: 'linear-gradient(180deg, #F4ECDB 0%, #ECE2C7 100%)' }}
  >
    {/* Paper grain texture overlay */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-multiply"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }}
    />

    {/* Top feathered edge → blend in from the dark hero above.
        Bottom feather removed: the next section (ReportPreviews) is dark and owns the
        cream→dark hand-off itself, so adding a dark band here created a visible stripe
        sandwich (cream → dark band → cream band → dark) at the seam. */}
    <div aria-hidden="true" className="pointer-events-none absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#28282B] to-transparent" />

    <div className="relative mx-auto max-w-7xl px-6 md:px-10">
      {/* Editorial header */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="max-w-3xl mb-20 md:mb-28"
      >
        <motion.div
          variants={wordFadeUp}
          custom={0}
          className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#6B5430] font-semibold mb-5 flex items-center gap-3"
        >
          <span className="block w-10 h-px bg-[#6B5430]" />
          Where Margin Slips
        </motion.div>
        <h2 className="font-display text-[#1F2A26] font-semibold tracking-tight text-4xl md:text-6xl leading-[1.02]">
          {HEADLINE_WORDS.slice(0, 3).map((w, i) => (
            <React.Fragment key={i}>
              <motion.span
                variants={wordFadeUp}
                custom={i + 1}
                className={`inline-block ${w.italic ? 'italic' : ''}`}
                style={w.color ? { color: w.color } : undefined}
              >
                {w.text}
              </motion.span>{' '}
            </React.Fragment>
          ))}
          <br />
          {HEADLINE_WORDS.slice(3).map((w, i) => (
            <React.Fragment key={i + 3}>
              <motion.span
                variants={wordFadeUp}
                custom={i + 4}
                className={`inline-block ${w.italic ? 'italic' : ''}`}
                style={w.color ? { color: w.color } : undefined}
              >
                {w.text}
              </motion.span>{' '}
            </React.Fragment>
          ))}
        </h2>
        <motion.p
          variants={wordFadeUp}
          custom={8}
          className="text-[#5A4A20]/85 text-base md:text-lg mt-7 leading-relaxed"
        >
          The best hospitality operators run at 8–14% EBITDA. The compression rarely comes from one big miss — it comes from a recurring set of small things that nobody sees because nobody is reading the right sources at the right cadence.
        </motion.p>
      </motion.div>

      {/* Cards — editorial 2-column layout with numbered serif badges */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 md:gap-y-20"
      >
        {LEAKS.map((leak, i) => (
          <motion.div
            key={leak.num}
            custom={i}
            variants={cardFadeUp}
            className="relative group"
            data-testid={`leak-card-${i}`}
          >
            <div className="flex items-baseline gap-5 mb-4">
              <span className="font-display italic text-[#C9A66B] text-5xl md:text-6xl leading-none select-none">
                {leak.num}
              </span>
              <span className="block flex-1 h-px bg-[#C9A66B]/40" />
            </div>
            <h3 className="font-display text-[#1F2A26] font-semibold text-2xl md:text-[28px] tracking-tight leading-tight mb-3">
              {leak.title}
            </h3>
            <svg
              aria-hidden="true"
              viewBox="0 0 120 8"
              className="block w-20 h-2 mb-4 opacity-70 group-hover:opacity-100 transition-opacity"
              preserveAspectRatio="none"
            >
              <path
                d="M2 5 C 22 1, 42 8, 62 4 S 102 1, 118 5"
                fill="none"
                stroke="#7A5F2E"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            <p className="text-[#3A3528] text-[15px] md:text-base leading-relaxed">
              {leak.desc}
            </p>
          </motion.div>
        ))}
      </motion.div>

      {/* Closing pull-quote */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9 }}
        className="mt-20 md:mt-28 max-w-2xl font-display italic text-[#5A4A20] text-xl md:text-2xl leading-relaxed border-l-2 border-[#C9A66B] pl-6"
      >
        Some of these are money to recover. Some are decisions to make. All of them are things you should have seen last week.
      </motion.p>
    </div>
  </section>
);

export default LeaksGrid;
